import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@library/components/card/Card';
import {
  DEFAULT_SIMULATIONS_LINE_ANALYZER_SORT,
  SIMULATIONS_LINE_ANALYZER_HEADER
} from '@bo-constants/simulations';
import type {
  OddsResponse,
  SimulationRun,
  SortSimulationLineAnalyzerColumn
} from '@library/types/simulations';
import { Table, TableBody, TableHeader } from '@library/components/table';
import { formatAmericanOdds, sortLineAnalyzer } from '@bo-utils/simulations';
import type { GameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { LineAnalysis } from '@bo-components/simulations/steps/StepTwoResults/LineAnalyzerRow';
import { LineAnalyzerRow } from '@bo-components/simulations/steps/StepTwoResults/LineAnalyzerRow';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { Searchbar } from '@library/components/inputs';
import { Select } from '@library/components/selects';
import type { SelectOption } from '@library/types/utils';
import { getNormalizedName } from '@library/utils/players';
import { partition } from 'ramda';
import { useState } from 'react';

export interface LineAnalyzerProps {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;
  simulation: SimulationRun;
  simulationOdds: OddsResponse;
}

export const LineAnalyzer = ({
  allMarkets,
  game,
  gamePlayers,
  simulation,
  simulationOdds
}: LineAnalyzerProps) => {
  const [homeSort, setHomeSort] = useState<SortSimulationLineAnalyzerColumn>(
    DEFAULT_SIMULATIONS_LINE_ANALYZER_SORT
  );

  const [awaySort, setAwaySort] = useState<SortSimulationLineAnalyzerColumn>(
    DEFAULT_SIMULATIONS_LINE_ANALYZER_SORT
  );

  const statTypeOptions: Array<SelectOption> = allMarkets.map(({ market }) => ({
    key: market,
    name: market,
    value: market
  }));

  const lineTypeOptions: Array<SelectOption> = [
    {
      key: 'Median',
      name: 'Median',
      value: 'Median'
    },
    {
      key: 'Alt',
      name: 'Alt',
      value: 'Alt'
    }
  ];

  const [searchPlayerValue, setSearchPlayerValue] = useState('');
  const [marketValue, setMarketValue] = useState<string>('1A');
  const [lineTypeValue, setLineTypeValue] = useState<string>('1A');

  const handleSort = ({
    home,
    sortColumn
  }: {
    home: boolean;
    sortColumn: SortSimulationLineAnalyzerColumn;
  }) => {
    if (home) {
      setHomeSort(sortColumn);
    } else {
      setAwaySort(sortColumn);
    }
  };

  const handleSearchPlayer = (value: string) => {
    setSearchPlayerValue(value);
  };

  const lineAnalyzerRows: Array<LineAnalysis> = simulationOdds.players.flatMap(
    (player) => {
      const { markets } = player;

      // TODO I removed player_id because it's the same as sportrader_player_id. Maybe it won't be one day.
      const {
        // prizepicks_player_id: playerId,
        sportradar_player_id: sportRadarId,
        player_name: playerName,
        team_name: team
      } = player;

      const { position } =
        gamePlayers.find((gamePlayer) => gamePlayer.name === playerName) || {};

      const lines = markets.flatMap((market) => {
        const statMap = allMarkets.find(
          (sampleMarket) => sampleMarket.market === market.market
        );

        const median = market.median_lines.simulation_runs.find(
          (simRun) => simRun.simulation_run_id === simulation.data.id
        );

        if (median === undefined) {
          return [];
        }

        const fanduelAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'FANDUEL'
        )?.odds.devigged.american_odds;

        const fanduel = formatAmericanOdds(fanduelAmericanOdds);

        const draftkingsAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'DRAFTKINGS'
        )?.odds.devigged.american_odds;

        const draftkings = formatAmericanOdds(draftkingsAmericanOdds);

        const underdogAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'UNDERDOG'
        )?.odds.devigged.american_odds;

        const underdog = formatAmericanOdds(underdogAmericanOdds);

        const pinnacleAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'PINNACLE'
        )?.odds.devigged.american_odds;

        const pinnacle = formatAmericanOdds(pinnacleAmericanOdds);

        const bet365AmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'BET365'
        )?.odds.devigged.american_odds;

        const bet365 = formatAmericanOdds(bet365AmericanOdds);

        const betMGMAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'BETMGM'
        )?.odds.devigged.american_odds;

        const betMGM = formatAmericanOdds(betMGMAmericanOdds);

        const circaAmericanOdds = market.third_parties?.find(
          (simRun) => simRun.originator === 'CIRCA'
        )?.odds.devigged.american_odds;

        const circa = formatAmericanOdds(circaAmericanOdds);

        const { line, odds } = median || {};

        const {
          american_odds: americanOdds = 0,
          implied_prob: impliedProb = '0'
        } = odds?.devigged || {};

        const deviggedAmericanOdds =
          americanOdds > 0 ? `+${americanOdds}` : String(americanOdds);

        const lineAnalysis: LineAnalysis = {
          // playerId,
          sportRadarId,
          playerName,
          position,
          normalizedPlayerName: playerName
            ? getNormalizedName({ name: playerName }).toUpperCase()
            : playerName,
          team,
          stat: statMap?.market || '',
          lineType: 'Median',
          line: line || '',
          market: market.market,
          fanduel,
          draftkings,
          underdog,
          pinnacle,
          bet365,
          betMGM,
          circa,
          currentlyPosted: market?.prizepicks_currently_posted?.line,
          deviggedAmericanOdds,
          impliedProb
        };

        const altLines: Array<LineAnalysis> = market.alt_lines.flatMap(
          (alternate) => {
            const alt = alternate.simulation_runs.find(
              (simRun) => simRun.simulation_run_id === simulation.data.id
            );

            const { line: altLine, odds: altOdds } = alt || {};

            if (!alt) {
              return [];
            }

            const {
              american_odds: altAmericanOdds = 0,
              implied_prob: altImpliedProb = '0'
            } = altOdds?.devigged || {};

            const deviggedAltAmericanOdds =
              altAmericanOdds > 0
                ? `+${altAmericanOdds}`
                : String(altAmericanOdds);

            return [
              {
                // playerId,
                sportRadarId,
                playerName,
                position,
                normalizedPlayerName: playerName
                  ? getNormalizedName({ name: playerName }).toUpperCase()
                  : playerName,
                team,
                stat: statMap?.market || '',
                lineType: 'Alt',
                line: altLine || '',
                market: market.market,
                fanduel,
                draftkings,
                underdog,
                pinnacle,
                bet365,
                betMGM,
                circa,
                currentlyPosted: market?.prizepicks_currently_posted?.line,
                deviggedAmericanOdds: deviggedAltAmericanOdds,
                impliedProb: altImpliedProb
              }
            ];
          }
        );

        return [lineAnalysis, ...altLines];
      });

      return lines;
    }
  );

  const filteredSearchPlayerRows = !searchPlayerValue
    ? lineAnalyzerRows
    : lineAnalyzerRows.filter((row) =>
        row.normalizedPlayerName.includes(searchPlayerValue.toUpperCase())
      );

  const filteredMarketRows =
    marketValue === '1A'
      ? filteredSearchPlayerRows
      : filteredSearchPlayerRows.filter((row) =>
          row.stat.toUpperCase().includes(marketValue.toUpperCase())
        );

  const filteredLineTypeRows =
    lineTypeValue === '1A'
      ? filteredMarketRows
      : filteredMarketRows.filter((row) =>
          row.lineType.toUpperCase().includes(lineTypeValue.toUpperCase())
        );

  const [homeTeamLineAnalyses, awayTeamLineAnalyses] = partition(
    (analysis) => analysis.team === game.home_team_name,
    filteredLineTypeRows
  );

  const homeRows = sortLineAnalyzer({
    lineAnalyses: homeTeamLineAnalyses,
    sort: homeSort
  });

  const awayRows = sortLineAnalyzer({
    lineAnalyses: awayTeamLineAnalyses,
    sort: awaySort
  });

  if (
    simulation.data.attributes.simulation_run_status ===
    'SIMULATION_IN_PROGRESS'
  ) {
    return (
      <div>
        <Loading
          loadingMessage="Your simulation is in Progress. If you want to make changes and cancel
          the current simulation, go back to the Build step."
        />
      </div>
    );
  }

  return (
    <Card className="text-soClean-100 sticky">
      <CardHeader>
        <CardTitle>Line Analyzer</CardTitle>
        <div className="flex gap-6 overflow-x-auto overscroll-x-none">
          <div className="w-fit shrink-0">
            <Select
              containerClassName="text-ellipsis overflow-hidden"
              allOption
              label="Markets"
              onChange={(statTypeOption) =>
                setMarketValue(statTypeOption.value)
              }
              options={statTypeOptions}
              portal
              size="small"
              value={marketValue}
            />
          </div>
          <div className="w-fit shrink-0">
            <Select
              allOption
              label="Line Types"
              onChange={(lineTypeOption) =>
                setLineTypeValue(lineTypeOption.value)
              }
              options={lineTypeOptions}
              portal
              size="small"
              value={lineTypeValue}
            />
          </div>
          <div className="w-80 shrink-0">
            <Searchbar
              searchValue={searchPlayerValue}
              handleSearch={handleSearchPlayer}
              disabled={false}
              placeholder="Search players"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid grid-flow-row gap-4">
        {lineAnalyzerRows.length === 0 && (
          <div>
            <NoResults label="Couldn't find results for this Simulation Run. This is most likely because the pricing service is being used against another sim - poke around or ask an engineer to help you find it!" />
          </div>
        )}
        {homeRows.length > 0 && (
          <>
            <span className="text-soClean-100">{game.home_team_name}</span>
            <Table>
              <div
                className="flex flex-col overflow-x-auto overscroll-x-none"
                data-testid="LineAnalyzer"
              >
                <TableHeader
                  className="bg-soFresh-110 sticky top-0 z-10 w-fit"
                  headers={SIMULATIONS_LINE_ANALYZER_HEADER}
                  sort={homeSort}
                  onSort={(sort) =>
                    handleSort({ home: true, sortColumn: sort })
                  }
                />
                <TableBody className="max-h-80 w-fit">
                  {homeRows.map((row) => (
                    <LineAnalyzerRow
                      key={`${row.playerName}-${row.market}-${row.lineType}`}
                      lineAnalysis={row}
                    />
                  ))}
                </TableBody>
              </div>
            </Table>
          </>
        )}

        {awayRows.length > 0 && (
          <>
            <span className="text-soClean-100">{game.away_team_name}</span>
            <Table>
              <div
                className="flex flex-col overflow-x-auto overscroll-x-none"
                data-testid="LineAnalyzer"
              >
                <TableHeader
                  className="bg-soFresh-110 sticky top-0 z-10 w-fit"
                  headers={SIMULATIONS_LINE_ANALYZER_HEADER}
                  sort={awaySort}
                  onSort={(sort) =>
                    handleSort({ home: false, sortColumn: sort })
                  }
                />
                <TableBody className="max-h-80 w-fit">
                  {awayRows.map((row) => (
                    <LineAnalyzerRow
                      key={`${row.playerName}-${row.market}-${row.lineType}`}
                      lineAnalysis={row}
                    />
                  ))}
                </TableBody>
              </div>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};
